import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import  React, { forwardRef, useState, useRef, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import Select, { components, Props as SelectProps, GroupBase } from 'react-select';

import BaseInput from './BaseInput';
import BaseInputProps from './BaseInputProps';


const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon />
    </components.DropdownIndicator>
  );
};

const ClearIndicator = (props: any) => {
  return (
    <components.ClearIndicator {...props}>
      <XMarkIcon />
    </components.ClearIndicator>
  );
};

const MultiValueRemove = (props: any) => {
  return (
    <components.MultiValueRemove {...props}>
      <XMarkIcon />
    </components.MultiValueRemove>
  );
};

const controlStyles = {
  base: 'bg-white mt-2 block w-full flex rounded-md border-0 pl-1 mr-3 text-limed-spruce-800 ring-1 ring-inset ring-porcelain-300 focus:ring-2 focus:ring-bondi-blue-600 text-base sm:leading-6',
  focus: 'border-primary-600 ring-1 ring-primary-500',
  nonFocus: 'border-gray-300 hover:border-gray-400',
};
const placeholderStyles = 'text-limed-spruce-800 pl-1 py-0.5';
const selectInputStyles = 'pl-1 py-0.5';
const valueContainerStyles = 'p-1 gap-1';
const singleValueStyles = 'leading-7 ml-1';
const multiValueStyles = 'bg-gray-100 rounded items-center py-0.5 pl-2 pr-1 gap-1.5';
const multiValueLabelStyles = 'leading-6 py-0.5';
const multiValueRemoveStyles =
  'border border-gray-200 bg-white w-5 h-5 hover:bg-red-50 hover:text-red-800 text-limed-spruce-800 hover:border-red-300 rounded-md';
const indicatorsContainerStyles = 'flex p-1 gap-1';
const clearIndicatorStyles = 'text-limed-spruce-800 p-1 w-6 h-6 rounded-md hover:bg-red-50 hover:text-red-800';
const indicatorSeparatorStyles = 'bg-gray-300';
const dropdownIndicatorStyles = 'p-1 hover:bg-gray-100 text-limed-spruce-800 w-6 h-6 rounded-md hover:text-black';
const menuStyles = 'p-1 mt-2 border border-gray-200 bg-white rounded-lg';
const groupHeadingStyles = 'ml-3 mt-2 mb-1 text-limed-spruce-800 text-sm';
const optionStyles = {
  base: 'hover:cursor-pointer px-3 py-2 rounded',
  focus: 'bg-gray-100 active:bg-gray-200',
  selected: "text-bondi-blue-600",
};
const noOptionsMessageStyles = 'text-mid-gray-500 p-2 bg-gray-50 border border-dashed border-gray-200 rounded-sm';

interface ReactSelectProps extends SelectProps<any, boolean, GroupBase<any>> {
  selectAllText?: string;
}

const ReactSelect = forwardRef(({ selectAllText, ...props
}: ReactSelectProps, ref: React.Ref<any>) => {
  const valueRef = useRef(props.value);
  valueRef.current = props.value;

  // https://stackoverflow.com/a/61250357 - Select All
  const methods = useMemo(() => {
    if (!selectAllText) {
      return { value: props.value, onChange: props.onChange, options: props.options };
    }
    
    const selectAllOption = {
      value: "<SELECT_ALL>",
      label: selectAllText,
    };

    const isSelectAllSelected = valueRef.current.length === props.options!.length;

    const isOptionSelected = option => (
      valueRef.current.some(({ value }) => value === option.value) || isSelectAllSelected
    );
  
    const options = [selectAllOption, ...props.options!];
  
    const value = isSelectAllSelected ? [selectAllOption] : props.value;
  
    const onChange = (newValue, actionMeta) => {
      const { action, option, removedValue } = actionMeta;
  
      if (action === "select-option" && option.value === selectAllOption.value) {
        props.onChange!(props.options, actionMeta);
      } else if (
        (action === "deselect-option" &&
          option.value === selectAllOption.value) ||
        (action === "remove-value" &&
          removedValue.value === selectAllOption.value)
      ) {
        props.onChange!([], actionMeta);
      } else if (
        actionMeta.action === "deselect-option" &&
        isSelectAllSelected
      ) {
        props.onChange!(
          props.options!.filter(({ value }) => value !== option.value),
          actionMeta
        );
      } else {
        props.onChange!(newValue || [], actionMeta);
      }
    };

    return { isOptionSelected, options, value, onChange };
  }, [props.onChange, props.options, props.value, selectAllText]);

  return (
    <Select
    ref={ref}
    isMulti
    closeMenuOnSelect={false}
    hideSelectedOptions={false}
    unstyled
    styles={{
      input: base => ({
        ...base,
        'input:focus': {
          boxShadow: 'none',
        },
      }),
      // On mobile, the label will truncate automatically, so we want to
      // override that behaviour.
      multiValueLabel: base => ({
        ...base,
        whiteSpace: 'normal',
        overflow: 'visible',
      }),
      control: base => ({
        ...base,
        minHeight: 34,
        transition: 'none',
      }),
    }}
    components={{ DropdownIndicator, ClearIndicator, MultiValueRemove }}
    classNames={{
      control: ({ isFocused }) => clsx(isFocused ? controlStyles.focus : controlStyles.nonFocus, controlStyles.base),
      placeholder: () => placeholderStyles,
      input: () => selectInputStyles,
      valueContainer: () => valueContainerStyles,
      singleValue: () => singleValueStyles,
      multiValue: () => multiValueStyles,
      multiValueLabel: () => multiValueLabelStyles,
      multiValueRemove: () => multiValueRemoveStyles,
      indicatorsContainer: () => indicatorsContainerStyles,
      clearIndicator: () => clearIndicatorStyles,
      indicatorSeparator: () => indicatorSeparatorStyles,
      dropdownIndicator: () => dropdownIndicatorStyles,
      menu: () => menuStyles,
      groupHeading: () => groupHeadingStyles,
      option: ({ isFocused, isSelected }) =>
        clsx(isFocused && optionStyles.focus, isSelected && optionStyles.selected, optionStyles.base),
      noOptionsMessage: () => noOptionsMessageStyles,
    }}
    {...props}
    {...methods}
  />);
});

ReactSelect.displayName = "ReactSelect";

export interface TextSelectInputProps extends BaseInputProps {
  options?: any[];
  placeholder?: string;
  maxSelect?: number;
  selectProps?: ReactSelectProps;
}

export default function TypeSelectInput({ 
  options = [], 
  placeholder = 'Select...',
  maxSelect,
  selectProps,
  ...baseProps
}: TextSelectInputProps) {
  const { name } = baseProps;
  const {
    control,
  } = useFormContext();
  const [isFocused, setIsFocused] = useState(false);

  return (
    <BaseInput {...{ isFocused, ...baseProps }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (<>
          <ReactSelect
            {...field}
            options={(maxSelect && field.value?.length >= maxSelect) ? options.filter(option => field.value?.includes(option.value)) : options}
            placeholder={placeholder}
            onChange={val => field.onChange(val.map((item: {
              value: string;
            }) => item.value))}
            value={options.filter(option => field.value?.includes(option.value))}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...selectProps}
            {...(maxSelect && { closeMenuOnSelect: true })}
          />
        </>
        )}
      />
    </BaseInput>
  );
}
